import {
  Cancel02Icon,
  GridTableIcon,
  LayoutGridIcon,
  Search02Icon,
} from '@hugeicons-pro/core-solid-rounded';
import { LicenseNoIcon, Megaphone01Icon } from '@hugeicons-pro/core-stroke-rounded';
import * as Linking from 'expo-linking';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useIsFocused, useScrollToTop } from 'expo-router/react-navigation';
import { FlashList } from '@shopify/flash-list';
import { InfiniteData, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { router } from 'expo-router';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, RefreshControl, Platform, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { PremiumUpgradeCard } from '@/components/domain/common/PremiumUpgradeCard';
import { MemberCard } from '@/components/domain/members/MemberCard';
import { MemberLockedDialog } from '@/components/domain/members/MemberLockedDialog';
import { MemberSearch } from '@/components/domain/members/MemberSearch';
import { NearbyFilterSelector } from '@/components/domain/members/NearbyFilterSelector';
import { getPeopleListHref, getPeopleListItems } from '@/components/domain/members/peopleListItems';
import { Box } from '@/components/gluestack/box';
import { VStack } from '@/components/gluestack/vstack';
import { ActionBar } from '@/components/layout/ActionBar';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { ButtonWrapper } from '@/components/ui/ButtonWrapper';
import { ErrorInListView } from '@/components/ui/ErrorInListView';
import { HeaderMenuSelector, HeaderMenuSelectorTitle } from '@/components/ui/HeaderMenuSelector';
import { HeaderRefreshButton } from '@/components/ui/HeaderRefreshButton';
import { MessageView } from '@/components/ui/MessageView';
import { useFlashListOptimizer } from '@/hooks/useFlashListOptimizer';
import { useHideOnScroll } from '@/hooks/useHideOnScroll';
import {
  useInfiniteListUtils,
  createSkeletonItems,
  clearStoredScrollPosition,
} from '@/hooks/useInfiniteListUtils';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import {
  PageParam,
  getNextPageSkipOrCursor,
  buildPaginationParams,
  dedupeById,
} from '@/hooks/usePaginationUtils';
import { useToastMessages } from '@/hooks/useToastMessages';
import { MembersView } from '@/models/Member';
import { MyTypeFilterMenu } from '~/components/domain/common/MyTypeFilterMenu';
import { HStack } from '~/components/gluestack/hstack';
import { NearbyFilterOption, PeopleListType } from '~/constants/lists';
import { ISearchCardV6, searchCardToQueryString } from '~/models/SearchCardV6';
import { MembersService } from '~/services/domain/MembersService';

const PEOPLE_DISPLAY_MODE_STORAGE_KEY = '@people_display_mode';
const COMPACT_DISPLAY_MODE = 'compact';
const REGULAR_DISPLAY_MODE = 'regular';

// Session cache for the display mode so only the first PeopleScreen mount pays the async
// storage read; later mounts know the column count on their first render. Without this the
// grid mounted at 2 columns and remounted at 3 (numColumns is part of the FlashList key)
// once the preference resolved — a visible double render on slower devices.
let cachedIsCompactModeEnabled: boolean | null = null;

interface PeopleScreenProps {
  listType: PeopleListType;
  nearbyFilter?: NearbyFilterOption;
  initialSearchCard?: ISearchCardV6 | null;
  lastUsedListType?: PeopleListType;
}

export function PeopleScreen({
  listType,
  nearbyFilter = 'recent',
  initialSearchCard = null,
  lastUsedListType = 'online',
}: PeopleScreenProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isLargeScreen = useIsLargeScreen();
  const { showSuccessToast, closeToast } = useToastMessages();
  const isScreenActive = useIsFocused();
  const flashListRef = useRef<React.ComponentRef<typeof FlashList<any>>>(null);
  const displayModeToastIdRef = useRef<string | null>(null);
  const { width } = useWindowDimensions();
  const { onEndReachedThreshold, pageSize } = useFlashListOptimizer({
    estimatedRowsOnScreen: { mobile: 4, desktop: 3 },
    maxColsOnScreen: { mobile: 3, desktop: 6 },
  });
  const { bottom: safeAreaBottom } = useSafeAreaInsets();

  const [activeSearchCard, setActiveSearchCard] = useState<ISearchCardV6 | null>(initialSearchCard);
  const [isCompactModeEnabled, setIsCompactModeEnabled] = useState<boolean | null>(
    cachedIsCompactModeEnabled
  );
  const [isLockedDialogOpen, setIsLockedDialogOpen] = useState(false);

  const isSearchMode = listType === 'search';
  const hasSearchResults = isSearchMode && activeSearchCard !== null;
  const isWebSearch = Platform.OS === 'web' && isSearchMode;
  const { data: searchOptions } = useQuery({
    queryKey: ['searchOptions'],
    queryFn: MembersService.getSearchOptions,
    enabled: isWebSearch,
    staleTime: 5 * 60 * 1000,
  });
  const massMessageUrl =
    isWebSearch && hasSearchResults ? searchOptions?.MassMessageUrl : undefined;
  const canMassMessage = Boolean(massMessageUrl);

  // Opens the admin panel's composer with the current search as recipients (base64url JSON)
  const handleSendMassMessage = useCallback(() => {
    if (!activeSearchCard || !massMessageUrl) return;
    const json = JSON.stringify(activeSearchCard);
    const base64 = btoa(String.fromCharCode(...new TextEncoder().encode(json)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
    void Linking.openURL(`${massMessageUrl}?card=${base64}`);
  }, [activeSearchCard, massMessageUrl]);
  const isCompactMode = !isLargeScreen && isCompactModeEnabled === true;
  // On large screens the column count comes from the window width, not the preference,
  // so the grid never has to wait for it there.
  const isDisplayModeReady = isLargeScreen || isCompactModeEnabled !== null;

  useEffect(() => {
    if (cachedIsCompactModeEnabled !== null) {
      setIsCompactModeEnabled(cachedIsCompactModeEnabled);
      return;
    }
    (async () => {
      try {
        const storedDisplayMode = await AsyncStorage.getItem(PEOPLE_DISPLAY_MODE_STORAGE_KEY);
        // A toggle may have set the cache while the read was in flight; don't clobber it.
        if (cachedIsCompactModeEnabled === null) {
          cachedIsCompactModeEnabled = storedDisplayMode === COMPACT_DISPLAY_MODE;
        }
      } catch (e) {
        console.error('Failed to load people display mode preference', e);
        cachedIsCompactModeEnabled = false;
      }
      setIsCompactModeEnabled(cachedIsCompactModeEnabled);
    })();
  }, []);

  // Use the infinite list utils hook
  const { handleRefresh, scrollToTop, isUserRefreshing, showHeaderRefetchIndicator, onScroll } =
    useInfiniteListUtils({
      queryKey: hasSearchResults
        ? ['members', 'search', activeSearchCard]
        : ['members', listType, ...(listType === 'nearby' ? [nearbyFilter] : [])],
      flashListRef,
      pageKey: hasSearchResults
        ? '/people/search/results'
        : `/people/${listType}${listType === 'nearby' ? `/${nearbyFilter}` : ''}`,
    });

  const { onScroll: onScrollHideTabBar } = useHideOnScroll();

  const handleScroll = useCallback(
    (e: any) => {
      onScroll?.(e);
      onScrollHideTabBar(e);
    },
    [onScroll, onScrollHideTabBar]
  );

  // Registers scroll to top for when the tab is pressed
  useScrollToTop(flashListRef);

  const getServiceMethod = (type: PeopleListType) => {
    switch (type) {
      case 'online':
        return MembersService.getOnlineMembers;
      case 'following':
        return MembersService.getFollowingMembers;
      case 'followers':
        return MembersService.getFollowersMembers;
      case 'visitors':
        return MembersService.getVisitorMembers;
      case 'new':
        return MembersService.getNewMembers;
      case 'birthdays':
        return MembersService.getBirthdayMembers;
      case 'nearby':
        return MembersService.getNearbyMembers;
      default:
        throw new Error(`Unknown list type: ${type}`);
    }
  };

  // Query for regular list types
  const regularQuery = useInfiniteQuery({
    queryKey: ['members', listType, ...(listType === 'nearby' ? [nearbyFilter] : [])],
    queryFn: async ({ pageParam }) => {
      const serviceMethod = getServiceMethod(listType);
      const params: any = {
        ...buildPaginationParams(pageParam, pageSize),
      };

      if (listType === 'nearby') {
        params.option = nearbyFilter;
      }

      return await serviceMethod(params);
    },
    enabled: !isSearchMode,
    initialPageParam: undefined as PageParam,
    getNextPageParam: getNextPageSkipOrCursor,
    select: useCallback((data: InfiniteData<MembersView, PageParam>) => {
      const items = dedupeById(data.pages.flatMap((page) => page.MembersBasic));
      const nonPremiumLimitReached = data.pages.some(
        (page) => page.NonPremiumLimitReached === true
      );
      return { ...data, items, nonPremiumLimitReached };
    }, []),
  });

  // Query for search results
  const searchQuery = useInfiniteQuery({
    queryKey: ['members', 'search', activeSearchCard],
    queryFn: async ({ pageParam }) => {
      if (!activeSearchCard) {
        throw new Error('No search card provided');
      }
      return await MembersService.searchMembers(
        activeSearchCard,
        buildPaginationParams(pageParam, pageSize)
      );
    },
    enabled: hasSearchResults,
    initialPageParam: undefined as PageParam,
    getNextPageParam: getNextPageSkipOrCursor,
    select: useCallback((data: InfiniteData<MembersView, PageParam>) => {
      const items = dedupeById(data.pages.flatMap((page) => page.MembersBasic));
      const nonPremiumLimitReached = data.pages.some(
        (page) => page.NonPremiumLimitReached === true
      );
      return { ...data, items, nonPremiumLimitReached };
    }, []),
  });

  // Select the appropriate query based on mode
  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isRefetching,
    isPending,
    refetch,
  } = hasSearchResults ? searchQuery : regularQuery;

  const skeletonItems = useMemo(() => createSkeletonItems(pageSize), [pageSize]);
  const peopleListItems = useMemo(() => getPeopleListItems(t), [t]);

  const navigateToList = useCallback(
    (newListType: PeopleListType) => {
      if (newListType === listType) return;

      queryClient.cancelQueries({ queryKey: ['members', listType] });

      // Sideways navigation between sibling lists should not restore the destination's
      // previous scroll position; only stack-pop returns (e.g. back from a profile) should.
      clearStoredScrollPosition(getPeopleListHref(newListType));

      if (newListType === 'search') {
        const fromParam = listType !== 'search' ? `?from=${listType}` : '';
        router.replace(`/people/search${fromParam}` as any);
      } else {
        router.replace(getPeopleListHref(newListType) as any);
      }
    },
    [listType, queryClient]
  );

  const handleSearch = useCallback(
    (searchCard: ISearchCardV6) => {
      if (Platform.OS === 'web') {
        // New searches should start at the top; only browser-back should restore scroll.
        clearStoredScrollPosition('/people/search/results');
        const queryString = searchCardToQueryString(searchCard);
        const fromParam = `from=${lastUsedListType}`;
        const href = queryString
          ? `/people/search/results?${queryString}&${fromParam}`
          : `/people/search/results?${fromParam}`;
        router.navigate(href as any);
      } else {
        setActiveSearchCard(searchCard);
        scrollToTop();
      }
    },
    [lastUsedListType, scrollToTop]
  );

  const handleCancelSearch = useCallback(() => {
    if (hasSearchResults) {
      if (Platform.OS === 'web') {
        clearStoredScrollPosition('/people/search');
        router.replace(`/people/search?from=${lastUsedListType}` as any);
      } else {
        setActiveSearchCard(null);
      }
    } else {
      clearStoredScrollPosition(getPeopleListHref(lastUsedListType));
      router.replace(getPeopleListHref(lastUsedListType) as any);
    }
  }, [hasSearchResults, lastUsedListType]);

  const handleToggleCompactMode = useCallback(() => {
    setIsCompactModeEnabled((value) => {
      const nextValue = !value;
      cachedIsCompactModeEnabled = nextValue;
      AsyncStorage.setItem(
        PEOPLE_DISPLAY_MODE_STORAGE_KEY,
        nextValue ? COMPACT_DISPLAY_MODE : REGULAR_DISPLAY_MODE
      ).catch((e) => {
        console.error('Failed to save people display mode preference', e);
      });
      if (displayModeToastIdRef.current) {
        closeToast(displayModeToastIdRef.current);
      }
      displayModeToastIdRef.current = showSuccessToast(
        nextValue ? t('people.displayModeCompact') : t('people.displayModeRegular'),
        undefined,
        'bottom'
      );
      return nextValue;
    });
  }, [closeToast, showSuccessToast, t]);

  const handleLockedMemberPress = useCallback(() => {
    setIsLockedDialogOpen(true);
  }, []);

  const handleLockedDialogClose = useCallback(() => {
    setIsLockedDialogOpen(false);
  }, []);

  const shouldShowRefreshButton = hasSearchResults || !isSearchMode;

  const ActionButtons = () => (
    <>
      {!isSearchMode && (
        <ButtonWrapper icon={Search02Icon} size="sm" onPress={() => navigateToList('search')} />
      )}
      {canMassMessage && (
        <ButtonWrapper
          icon={Megaphone01Icon}
          text="Send mass message"
          size="sm"
          action="neutral"
          iconClassName="color-typography-500"
          onPress={handleSendMassMessage}
        />
      )}
      {isSearchMode && (!isLargeScreen || hasSearchResults) && (
        <ButtonWrapper
          icon={Cancel02Icon}
          size="sm"
          action="neutral"
          iconClassName="color-typography-500"
          onPress={handleCancelSearch}
        />
      )}
    </>
  );

  const DisplayModeButton = () => (
    <ButtonWrapper
      icon={isCompactMode ? LayoutGridIcon : GridTableIcon}
      size="sm"
      action="neutral"
      iconClassName="color-typography-500"
      onPress={handleToggleCompactMode}
    />
  );

  // Determine number of columns based on screen size (only 2, 3, 4, or 6 columns)
  const getNumColumns = () => {
    if (!isLargeScreen) {
      return isCompactMode ? 3 : 2;
    }
    if (width >= 1536) {
      return 6;
    } else if (width >= 1024) {
      return 4;
    } else if (width >= 768) {
      return 3;
    }
    return 2;
  };
  const numColumns = getNumColumns();

  // Memoize ItemWrapper to prevent re-renders
  const ItemWrapper = useMemo(
    () =>
      ({ children }: { children: React.ReactNode }) => (
        <Box
          className={
            isCompactMode ? 'mt-2 flex-1 pr-2' : 'mt-3 flex-1 pr-3 md:mb-2 md:mt-2 md:pr-4'
          }>
          {children}
        </Box>
      ),
    [isCompactMode]
  );

  // Shared style for FlashList container
  const flashListContainerStyle = useMemo(
    () => ({
      paddingLeft: isLargeScreen ? 16 : 11,
      paddingRight: isCompactMode ? 3 : 0,
      paddingBottom: isLargeScreen ? safeAreaBottom : 0,
    }),
    [isCompactMode, isLargeScreen, safeAreaBottom]
  );

  // Get item count for the CountBadge
  const getItemCount = useCallback(() => {
    if (listType === 'nearby' || isSearchMode) return 0;
    const queryData = queryClient.getQueryData(['members', listType]) as any;
    return queryData?.pages?.[0]?.ItemCount || 0;
  }, [queryClient, listType, isSearchMode]);

  const itemCount = getItemCount();

  const isMyTypeFiltered = useMemo(() => {
    return (data?.pages?.[0] as any)?.IsMyTypeFiltered ?? null;
  }, [data]);

  // Get search results count
  const searchResultsCount = useMemo(() => {
    if (!hasSearchResults || !data) return 0;
    // Some search modes (e.g. location-based) return ItemCount: null.
    // In that case we should not show a count badge (CountBadge hides for <= 0).
    return data.pages?.[0]?.ItemCount ?? 0;
  }, [hasSearchResults, data]);

  // Handle nearby filter change
  const handleNearbyFilterChange = (newFilter: NearbyFilterOption) => {
    if (newFilter === nearbyFilter) return;

    queryClient.cancelQueries({ queryKey: ['members', 'nearby', nearbyFilter] });

    clearStoredScrollPosition(`/people/nearby/${newFilter}`);
    router.replace(`/people/nearby/${newFilter}` as any);
  };

  const renderMemberItem = useCallback(
    ({ item }: { item: any }) => (
      <ItemWrapper>
        {isPending ? (
          <MemberCard compact={isCompactMode} />
        ) : (
          <MemberCard
            member={item}
            compact={isCompactMode}
            onLockedPress={handleLockedMemberPress}
          />
        )}
      </ItemWrapper>
    ),
    [handleLockedMemberPress, isCompactMode, isPending, ItemWrapper]
  );

  const keyExtractor = useCallback(
    (item: any, index: number) => (isPending ? `s-${index}` : `member-${item.Id}`),
    [isPending]
  );

  const handleEndReached = useCallback(() => {
    if (!isPending && hasNextPage && isScreenActive && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isPending, isScreenActive]);

  const listEmptyComponent = useMemo(
    () => <MessageView type="warning" text={t('people.empty')} icon={LicenseNoIcon} listEmpty />,
    [t]
  );

  const renderListFooter = useCallback(
    () => (
      <Box style={{ width: '100%' }}>
        {isFetchingNextPage && (
          <Box className="m-4">
            <ActivityIndicator color="grey" />
          </Box>
        )}
        {!isPending && data?.nonPremiumLimitReached && (
          <Box className="my-4 mr-3">
            <PremiumUpgradeCard listType="members" />
          </Box>
        )}
      </Box>
    ),
    [data?.nonPremiumLimitReached, isFetchingNextPage, isPending]
  );

  return (
    <VStack className="flex-1">
      <ScreenHeader
        options={{
          hideBackButton: true,
          title: isLargeScreen ? t(`people.${listType}`) : t('navigation.people'),
          ...(isLargeScreen
            ? {
                headerTitle: () => (
                  <HeaderMenuSelectorTitle
                    value={listType}
                    items={peopleListItems}
                    title={t(`people.${listType}`)}
                  />
                ),
                headerLeft: () => (
                  <HStack className="items-center gap-3">
                    {listType === 'nearby' && (
                      <NearbyFilterSelector
                        filterOption={nearbyFilter}
                        onFilterChange={handleNearbyFilterChange}
                      />
                    )}
                    {hasSearchResults && (
                      <MyTypeFilterMenu
                        count={searchResultsCount}
                        isMyTypeFiltered={isMyTypeFiltered}
                      />
                    )}
                    {!isSearchMode && (
                      <MyTypeFilterMenu count={itemCount} isMyTypeFiltered={isMyTypeFiltered} />
                    )}
                    {showHeaderRefetchIndicator && <ActivityIndicator color="grey" />}
                  </HStack>
                ),
                headerRight: () => (
                  <HStack className="items-center gap-3">
                    <ActionButtons />
                    <HeaderRefreshButton
                      isVisible={shouldShowRefreshButton}
                      isLargeScreen={isLargeScreen}
                      isRefreshing={isUserRefreshing}
                      isDisabled={isRefetching}
                      inAppHeaderBar={!isLargeScreen}
                      onPress={handleRefresh}
                    />
                  </HStack>
                ),
              }
            : {
                headerRight: () => (
                  <HeaderRefreshButton
                    isVisible={shouldShowRefreshButton}
                    isLargeScreen={isLargeScreen}
                    isRefreshing={isUserRefreshing}
                    isDisabled={isRefetching}
                    inAppHeaderBar
                    onPress={handleRefresh}
                  />
                ),
                headerBottom: () => (
                  <ActionBar
                    leftSection={
                      <HStack className="items-center gap-3 pr-3">
                        <HeaderMenuSelector
                          value={listType}
                          onChange={navigateToList}
                          items={peopleListItems}
                          triggerLabel={t(`people.${listType}_short`)}
                        />
                        {listType === 'nearby' && (
                          <NearbyFilterSelector
                            filterOption={nearbyFilter}
                            onFilterChange={handleNearbyFilterChange}
                          />
                        )}
                        {hasSearchResults && (
                          <MyTypeFilterMenu
                            count={searchResultsCount}
                            isMyTypeFiltered={isMyTypeFiltered}
                          />
                        )}
                        {!isSearchMode && (
                          <MyTypeFilterMenu count={itemCount} isMyTypeFiltered={isMyTypeFiltered} />
                        )}
                      </HStack>
                    }
                    rightSection={
                      <HStack className="items-center gap-3">
                        {(hasSearchResults || !isSearchMode) && <DisplayModeButton />}
                        <ActionButtons />
                      </HStack>
                    }
                  />
                ),
              }),
        }}
      />

      {isSearchMode && !hasSearchResults ? (
        <MemberSearch onSearch={handleSearch} isSearching={searchQuery.isFetching} />
      ) : error ? (
        <ErrorInListView error={error} onRetry={() => refetch()} isRetrying={isRefetching} />
      ) : !isDisplayModeReady ? null : (
        <FlashList
          key={`people-list-${numColumns}`}
          ref={flashListRef}
          data={isPending ? skeletonItems : data?.items}
          renderItem={renderMemberItem}
          keyExtractor={keyExtractor}
          numColumns={numColumns}
          onEndReached={handleEndReached}
          maintainVisibleContentPosition={{
            autoscrollToTopThreshold: 10,
          }}
          onEndReachedThreshold={onEndReachedThreshold}
          ListEmptyComponent={listEmptyComponent}
          ListFooterComponent={renderListFooter}
          refreshControl={
            <RefreshControl refreshing={isUserRefreshing} onRefresh={handleRefresh} />
          }
          contentContainerStyle={flashListContainerStyle}
          onScroll={handleScroll}
        />
      )}
      {/* Keep one shared dialog here instead of mounting one per recycled MemberCard. */}
      <MemberLockedDialog isOpen={isLockedDialogOpen} onClose={handleLockedDialogClose} />
    </VStack>
  );
}
